/**
 * Application Configuration
 * 
 * This module provides centralized configuration management for the TUI ReportHub application.
 * It supports both build-time environment variables (Vite) and runtime configuration (public/config.js).
 * 
 * Priority:
 * 1. Runtime config (window.APP_CONFIG) - Used in deployed environments
 * 2. Build-time env vars (import.meta.env) - Used in local development
 * 3. Defaults - Fallback values
 */

// Load runtime configuration if available (deployed environment)
const runtimeConfig = window.APP_CONFIG || {};

// Configuration object
const config = {
  // AWS Cognito Configuration
  cognito: {
    region: runtimeConfig.cognito?.region || import.meta.env.VITE_AWS_REGION || 'eu-central-1',
    userPoolId: runtimeConfig.cognito?.userPoolId || import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
    userPoolWebClientId: runtimeConfig.cognito?.userPoolWebClientId || import.meta.env.VITE_COGNITO_CLIENT_ID || '',
    domain: runtimeConfig.cognito?.domain || import.meta.env.VITE_COGNITO_DOMAIN || '',
    oauth: {
      domain: runtimeConfig.cognito?.oauth?.domain || `${import.meta.env.VITE_COGNITO_DOMAIN}.auth.${import.meta.env.VITE_AWS_REGION}.amazoncognito.com`,
      scope: runtimeConfig.cognito?.oauth?.scope || ['email', 'openid', 'profile'],
      redirectSignIn: runtimeConfig.cognito?.oauth?.redirectSignIn || import.meta.env.VITE_OAUTH_REDIRECT_SIGN_IN || window.location.origin,
      redirectSignOut: runtimeConfig.cognito?.oauth?.redirectSignOut || import.meta.env.VITE_OAUTH_REDIRECT_SIGN_OUT || `${window.location.origin}/login?signed-out=true`,
      responseType: runtimeConfig.cognito?.oauth?.responseType || 'code'
    }
  },

  // API Configuration
  api: {
    baseUrl: runtimeConfig.api?.baseUrl || import.meta.env.VITE_API_BASE_URL || '/api/v1',
    timeout: runtimeConfig.api?.timeout || import.meta.env.VITE_API_TIMEOUT || 30000
  },

  // Application Configuration
  app: {
    name: runtimeConfig.app?.name || import.meta.env.VITE_APP_NAME || 'TUI ReportHub',
    version: runtimeConfig.app?.version || import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: runtimeConfig.app?.environment || import.meta.env.VITE_ENVIRONMENT || 'development'
  },

  // Feature Flags
  features: {
    enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
    enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true'
  },

  // SSO Configuration
  sso: {
    enabled: runtimeConfig.sso?.enabled ?? (import.meta.env.VITE_SSO_ENABLED === 'true')
  },

  // SFTP Configuration
  sftp: {
    endpoint: runtimeConfig.sftp?.endpoint || import.meta.env.VITE_SFTP_ENDPOINT || ''
  }
};

// Validation helper
export const validateConfig = () => {
  const errors = [];

  if (!config.cognito.userPoolId) {
    errors.push('Missing Cognito User Pool ID');
  }

  if (!config.cognito.userPoolWebClientId) {
    errors.push('Missing Cognito Client ID');
  }

  if (!config.cognito.domain) {
    errors.push('Missing Cognito Domain');
  }

  if (errors.length > 0) {
    console.warn('Configuration validation warnings:', errors);
    console.warn('Some features may not work correctly. Check your environment variables or public/config.js');
  }

  return errors.length === 0;
};

// Log configuration in development
if (config.app.environment === 'development' || config.features.enableDebug) {
  console.log('App Configuration:', {
    ...config,
    cognito: {
      ...config.cognito,
      userPoolWebClientId: config.cognito.userPoolWebClientId ? '***' : 'NOT SET'
    }
  });
}

export default config;
